import { useEffect } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Theme } from '@radix-ui/themes';
import '@radix-ui/themes/styles.css';
import { Flex, Spinner, Text } from '@radix-ui/themes';
import { AgentProvider, ModelContextProvider } from '@getdutchie/agent-react';
import { SYSTEM_PROMPT } from '@/shared/constants';
import { Shell } from '@/ui/shell';
import { DrawerOverlay } from '@/ui/drawer/drawer-overlay';
import { setSession, type Session } from '@/api/session';
import { getJwt } from '@/api/endpoints';

export interface AppProps {
  /** Session injected from host or withDevLogin HOC. */
  session: Session;
  /** Base URL for the agent backend (e.g. http://localhost:5420). /agent is appended. */
  agentUrl?: string;
  /** When provided, the app renders inside a drawer and this callback unmounts it. */
  onClose?: () => void;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false },
  },
});

export default function App({ session, agentUrl, onClose }: AppProps) {
  const baseAgentUrl = agentUrl || import.meta.env.PUBLIC_AGENT_URL || 'http://localhost:5420';
  const resolvedAgentUrl = `${baseAgentUrl}/agent`;

  const content = (
    <QueryClientProvider client={queryClient}>
      <Theme accentColor="gray">
        <AgentApp session={session} agentUrl={resolvedAgentUrl} onClose={onClose} />
      </Theme>
    </QueryClientProvider>
  );

  if (onClose) {
    return <DrawerOverlay onClose={onClose}>{content}</DrawerOverlay>;
  }

  return content;
}

function AgentApp({
  session,
  agentUrl,
  onClose,
}: {
  session: Session;
  agentUrl: string;
  onClose?: () => void;
}) {
  useEffect(() => {
    setSession(session);
  }, [session]);

  const {
    data: jwt,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ['jwt'],
    queryFn: getJwt,
    staleTime: 4 * 60_000,
    refetchOnWindowFocus: false,
  });

  if (isPending) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100vh' }}>
        <Spinner size="3" />
      </Flex>
    );
  }

  if (isError || !jwt) {
    return (
      <Flex
        align="center"
        justify="center"
        direction="column"
        gap="3"
        style={{ minHeight: '100vh' }}
      >
        <Text size="3" color="red">
          Failed to establish agent session
        </Text>
        <Text size="2" color="gray">
          {error?.message}
        </Text>
      </Flex>
    );
  }

  return (
    <ModelContextProvider>
      <AgentProvider agentUrl={agentUrl} authToken={jwt} autoConnect prompt={SYSTEM_PROMPT}>
        <Shell onClose={onClose} />
      </AgentProvider>
    </ModelContextProvider>
  );
}
